import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DeliveryInformation, GetDeliveryMomentsResponse, OrderingInformation } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DIALOG_RETURN_TYPES, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';

import { RepeatOrderItem } from 'src/app/models/repeat-order-item.model';
import { ProductDeliveryInformationComponent } from 'src/app/product/components/product-delivery-information/product-delivery-information.component';
import * as AppActions from 'src/app/store/actions';

import { RepeatOrderType } from '../../pages/abstract-repeat-order-page-component.directive';

interface RepeatOrderDeliveryDateData extends AbstractDialogData {
  repeatOrderItem?: RepeatOrderItem;
  repeatOrderType: RepeatOrderType;
  deliveryInformation?: DeliveryInformation;
}

@UntilDestroy()
@Component({
  selector: 'app-repeat-order-delivery-date',
  templateUrl: 'delivery-date.component.html',
  styleUrls: ['delivery-date.component.scss'],
  imports: [AgrDialogModule, ProductDeliveryInformationComponent],
})
export class RepeatOrderDeliveryDateComponent extends AbstractDialog<RepeatOrderDeliveryDateData> implements OnInit {
  deliveryMomentsResponse: GetDeliveryMomentsResponse;
  deliveryInformation: DeliveryInformation | null;
  orderingInformation: OrderingInformation;
  chosenFlexibility: number | null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RepeatOrderDeliveryDateData,
    protected readonly mdDialogRef: MatDialogRef<RepeatOrderDeliveryDateComponent, AbstractDialogResultData<RepeatOrderDeliveryDateData>>,
    private readonly localeService: LocaleService,
    private readonly store: Store,
  ) {
    super();
    this.deliveryInformation = data.deliveryInformation;
    this.orderingInformation = data.repeatOrderItem?.orderingInformation;
    this.chosenFlexibility = data.repeatOrderItem?.deliveryInformation?.flexibility ?? null;
  }

  ngOnInit(): void {
    this.data.title = this.localeService.translate('delivery-date.tab-title');
    this.data.subTitle = this.data.repeatOrderItem?.title;
    this.data.confirmButton = true;
    this.data.confirmButtonText = this.localeService.translate('delivery-date.confirm');
  }

  cancel() {
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel });
  }

  confirmDeliveryDate() {
    if (!this.deliveryInformation) return;
    let selectedDeliveryInformation = this.deliveryInformation;

    const formattedDeliveryInfo = {
      ...selectedDeliveryInformation,
      deliveryDate: selectedDeliveryInformation.deliveryDate.endsWith('Z')
        ? selectedDeliveryInformation.deliveryDate
        : selectedDeliveryInformation.deliveryDate.split('.')[0] + 'Z',
    };

    if (this.data.repeatOrderType === RepeatOrderType.bulk) {
      this.store.dispatch(
        AppActions.updateBulkDeliveryInformation({
          deliveryInformation: formattedDeliveryInfo,
          internalId: this.data.repeatOrderItem?.internalId,
        }),
      );
    } else {
      this.store.dispatch(
        AppActions.updateCargoDeliveryInformation({
          deliveryInformation: formattedDeliveryInfo,
          internalId: this.data.repeatOrderItem?.internalId,
        }),
      );
    }

    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm });
  }

  handleDeliveryInformationChange(selectedDeliveryInformation: DeliveryInformation): void {
    this.deliveryInformation = {
      ...selectedDeliveryInformation,
      deliveryDate: selectedDeliveryInformation.deliveryDate.split('.')[0] + 'Z',
      possibleDeliveryDays: selectedDeliveryInformation.possibleDeliveryDays,
    };
  }

  handleDeliveryInformationIsValidChange(isDataValid: boolean): void {
    this.data.disableConfirmButton = !isDataValid;
  }
}
